/**
 * /admin/$slug/my-availability — staff self-service availability page.
 * ADR-0014 V2 Phase 4.
 *
 * Visible to all roles (staff, admin, owner). Two sections:
 *   A. Working pattern — weekly grid Mon–Sun, per-day open/closed +
 *      start/end times. Writes `availability_rules` (one row per open
 *      day) via /api/admin/:slug/availability/rules/replace.
 *   B. Time off — list of upcoming overrides + an Add time off button.
 *      Writes `availability_overrides`.
 *
 * Owners and admins can edit anyone via `?userId=<id>` — used by the
 * Roster overview to drill into "edit this staff member's working
 * pattern" without a separate API surface. Staff can only ever edit
 * their own; the server enforces, this is UX.
 */
import {
  Link,
  createFileRoute,
  useSearch,
} from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormField,
  Input,
  Textarea,
} from '@bookaburra/ui';
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  Clock,
  Info,
  Plus,
  Trash2,
} from 'lucide-react';
import {
  AvailabilityApiError,
  type AvailabilityOverride,
  type AvailabilityRule,
  type AvailabilityStaff,
  createOverride,
  deleteOverride,
  fetchAvailability,
  replaceRules,
} from '../booking-flow/availability-api';
import { fetchMyMemberships } from '../booking-flow/tenant-api';
import { fetchTenantSettings } from '../booking-flow/tenant-settings-api';
import { fetchBookingTenant } from '../booking-flow/api';
import { useDocumentTitle } from '../lib/use-document-title';

export const Route = createFileRoute('/admin/$slug/my-availability')({
  component: MyAvailabilityPage,
  validateSearch: (
    s: Record<string, unknown>,
  ): { userId?: string } => ({
    userId: typeof s.userId === 'string' ? s.userId : undefined,
  }),
});

const DAYS: Array<{ key: DayKey; label: string; isoDay: number; mask: number }> = [
  { key: 'mon', label: 'Monday', isoDay: 1, mask: 1 << 0 },
  { key: 'tue', label: 'Tuesday', isoDay: 2, mask: 1 << 1 },
  { key: 'wed', label: 'Wednesday', isoDay: 3, mask: 1 << 2 },
  { key: 'thu', label: 'Thursday', isoDay: 4, mask: 1 << 3 },
  { key: 'fri', label: 'Friday', isoDay: 5, mask: 1 << 4 },
  { key: 'sat', label: 'Saturday', isoDay: 6, mask: 1 << 5 },
  { key: 'sun', label: 'Sunday', isoDay: 7, mask: 1 << 6 },
];

type DayKey = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';

interface DayRow {
  open: boolean;
  openMinute: number;
  closeMinute: number;
}

type WeeklyPattern = Record<DayKey, DayRow>;

const DEFAULT_DAY_ROW: DayRow = { open: true, openMinute: 9 * 60, closeMinute: 17 * 60 };
const CLOSED_DAY_ROW: DayRow = { open: false, openMinute: 9 * 60, closeMinute: 17 * 60 };

function MyAvailabilityPage() {
  const { slug } = Route.useParams();
  const { userId: queryUserId } = useSearch({ from: Route.id });
  const qc = useQueryClient();

  const meQ = useQuery({
    queryKey: ['me-memberships'],
    queryFn: fetchMyMemberships,
    staleTime: 60_000,
  });
  const myUserId = meQ.data?.user.id;
  const currentRole = meQ.data?.memberships.find((m) => m.tenant.slug === slug)?.role;
  const isAdmin = currentRole === 'owner' || currentRole === 'manager';

  // Owners/admins can edit anyone via ?userId=; staff always edit
  // themselves regardless of what the URL says.
  const effectiveUserId = isAdmin && queryUserId ? queryUserId : myUserId;
  const isEditingOther = isAdmin && !!queryUserId && queryUserId !== myUserId;

  const availQ = useQuery({
    queryKey: ['availability', slug],
    queryFn: () => fetchAvailability(slug),
  });
  const tenantQ = useQuery({
    queryKey: ['booking-tenant', slug],
    queryFn: () => fetchBookingTenant(slug),
    staleTime: 60_000,
  });
  // Tenant settings — surfaces business_hours (default for new staff)
  // and require_time_off_approval (whether self-submits go pending).
  const settingsQ = useQuery({
    queryKey: ['admin-tenant', slug],
    queryFn: () => fetchTenantSettings(slug),
    enabled: !!effectiveUserId,
  });

  useDocumentTitle(
    tenantQ.data
      ? `My availability · ${tenantQ.data.tenant.name} Admin`
      : 'My availability',
  );

  const targetStaff: AvailabilityStaff | undefined = useMemo(() => {
    if (!availQ.data || !effectiveUserId) return undefined;
    return availQ.data.staff.find((s) => s.id === effectiveUserId);
  }, [availQ.data, effectiveUserId]);

  const myRules = useMemo<AvailabilityRule[]>(() => {
    if (!availQ.data || !effectiveUserId) return [];
    return availQ.data.rules.filter((r) => r.staffId === effectiveUserId);
  }, [availQ.data, effectiveUserId]);

  const myOverrides = useMemo<AvailabilityOverride[]>(() => {
    if (!availQ.data || !effectiveUserId) return [];
    const now = Date.now();
    return availQ.data.overrides
      .filter((o) => o.staffId === effectiveUserId && o.rangeEnd >= now)
      .sort((a, b) => a.rangeStart - b.rangeStart);
  }, [availQ.data, effectiveUserId]);

  // Compute the initial weekly pattern: prefer existing rules; fall
  // back to tenant business hours; final fallback Mon-Fri 9-5.
  const initialPattern = useMemo<WeeklyPattern>(() => {
    if (myRules.length > 0) return rulesToPattern(myRules);
    return businessHoursFallback(settingsQ.data?.tenant.businessHours ?? null);
  }, [myRules, settingsQ.data]);

  const [pattern, setPattern] = useState<WeeklyPattern>(initialPattern);
  const [patternInitialised, setPatternInitialised] = useState(false);

  // Mirror initial pattern once availability has loaded, then leave the
  // form alone — background refetches must not nuke unsaved edits.
  useEffect(() => {
    if (patternInitialised) return;
    if (!availQ.data) return;
    if (!effectiveUserId) return;
    setPattern(initialPattern);
    setPatternInitialised(true);
  }, [availQ.data, effectiveUserId, initialPattern, patternInitialised]);

  // When the editor switches target users (e.g. owner navigates
  // ?userId=other), force a re-sync of the local pattern state.
  useEffect(() => {
    setPatternInitialised(false);
  }, [effectiveUserId]);

  const replaceMut = useMutation({
    mutationFn: () =>
      replaceRules(slug, {
        staffId: effectiveUserId!,
        rules: patternToRules(pattern, tenantQ.data?.tenant.timezone ?? 'Australia/Sydney'),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['availability', slug] });
    },
  });

  const [addOpen, setAddOpen] = useState(false);

  const requireApproval = settingsQ.data?.tenant.requireTimeOffApproval ?? false;
  const isSelfEditing = !isEditingOther;

  if (meQ.isLoading || availQ.isLoading) {
    return (
      <main className="mx-auto max-w-4xl px-6 py-10 sm:px-12">
        <div className="h-64 animate-pulse rounded-2xl bg-secondary" />
      </main>
    );
  }
  if (!effectiveUserId) {
    return (
      <main className="mx-auto max-w-4xl px-6 py-10 sm:px-12">
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Not signed in</AlertTitle>
          <AlertDescription>Sign in to view your availability.</AlertDescription>
        </Alert>
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-4xl px-6 py-10 sm:px-12">
      <header className="mb-8">
        {isEditingOther ? (
          <Link
            to="/admin/$slug/roster"
            params={{ slug }}
            className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" /> Back to roster
          </Link>
        ) : (
          <Link
            to="/admin/$slug/my-day"
            params={{ slug }}
            className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" /> Back to my day
          </Link>
        )}
        <h1 className="mt-2 flex items-center gap-2 text-3xl font-bold tracking-tight">
          <Clock className="h-7 w-7 text-muted-foreground" />
          <span>
            {isEditingOther
              ? `${targetStaff?.name ?? 'Staff'}'s availability`
              : 'My availability'}
          </span>
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {isEditingOther
            ? 'You can edit working pattern + time off on behalf of this staff member.'
            : 'Set the days and times you work. Add time off when you need a day. Changes show up in the allocator straight away.'}
        </p>
        {requireApproval && isSelfEditing ? (
          <Alert className="mt-4">
            <Info className="h-4 w-4" />
            <AlertTitle>Time off needs approval</AlertTitle>
            <AlertDescription>
              This business has time-off approval turned on. Your requests
              land as Pending and an owner or admin needs to approve
              before they block bookings.
            </AlertDescription>
          </Alert>
        ) : null}
      </header>

      <section className="mb-10">
        <h2 className="mb-3 text-xl font-semibold">Working pattern</h2>
        <Card className="p-6">
          <p className="mb-4 text-sm text-muted-foreground">
            Defaults come from the business hours. Tick a day, then enter
            your start and finish times. Untick days you're not working.
          </p>
          <div className="grid gap-2">
            {DAYS.map((d) => (
              <DayRowEditor
                key={d.key}
                label={d.label}
                value={pattern[d.key]}
                onChange={(next) =>
                  setPattern((prev) => ({ ...prev, [d.key]: next }))
                }
              />
            ))}
          </div>
          {replaceMut.error ? (
            <Alert variant="destructive" className="mt-4">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Couldn't save</AlertTitle>
              <AlertDescription>
                {(replaceMut.error as Error).message}
              </AlertDescription>
            </Alert>
          ) : null}
          {replaceMut.isSuccess && !replaceMut.isPending ? (
            <Alert variant="success" className="mt-4">
              <CheckCircle2 className="h-4 w-4" />
              <AlertTitle>Saved</AlertTitle>
              <AlertDescription>
                Your working pattern is updated. New bookings will respect
                the new hours immediately.
              </AlertDescription>
            </Alert>
          ) : null}
          <div className="mt-6 flex items-center justify-end gap-3">
            <Button
              type="button"
              onClick={() => replaceMut.mutate()}
              disabled={replaceMut.isPending || !patternIsValid(pattern)}
            >
              {replaceMut.isPending ? 'Saving…' : 'Save working pattern'}
            </Button>
          </div>
        </Card>
      </section>

      <section>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-xl font-semibold">Time off</h2>
          <Button type="button" onClick={() => setAddOpen(true)}>
            <Plus className="mr-1 h-4 w-4" /> Add time off
          </Button>
        </div>
        <Card className="p-6">
          {myOverrides.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No upcoming time off booked.
            </p>
          ) : (
            <ul className="divide-y divide-brand-sage/15">
              {myOverrides.map((o) => (
                <OverrideRow
                  key={o.id}
                  slug={slug}
                  override={o}
                  canDelete={isAdmin || isSelfEditing}
                  timezone={tenantQ.data?.tenant.timezone ?? 'Australia/Sydney'}
                />
              ))}
            </ul>
          )}
        </Card>
      </section>

      {addOpen ? (
        <AddOverrideDialog
          slug={slug}
          staffId={effectiveUserId}
          staffName={targetStaff?.name ?? 'You'}
          timezone={tenantQ.data?.tenant.timezone ?? 'Australia/Sydney'}
          willPending={requireApproval && isSelfEditing}
          onClose={() => setAddOpen(false)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['availability', slug] });
            setAddOpen(false);
          }}
        />
      ) : null}
    </main>
  );
}

function DayRowEditor({
  label,
  value,
  onChange,
}: {
  label: string;
  value: DayRow;
  onChange: (next: DayRow) => void;
}) {
  return (
    <div className="grid grid-cols-[auto_140px_1fr] items-center gap-3 rounded-md border border-brand-sage/15 bg-brand-cream/40 p-3">
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={value.open}
          onChange={(e) => onChange({ ...value, open: e.target.checked })}
          className="h-4 w-4 rounded border-input"
        />
        <span className="w-24 font-medium">{label}</span>
      </label>
      {value.open ? (
        <div className="flex items-center gap-2 text-sm">
          <input
            type="time"
            value={toTimeString(value.openMinute)}
            onChange={(e) =>
              onChange({ ...value, openMinute: fromTimeString(e.target.value) })
            }
            className="h-9 rounded-md border border-input bg-background px-2 text-sm"
          />
          <span className="text-muted-foreground">to</span>
          <input
            type="time"
            value={toTimeString(value.closeMinute)}
            onChange={(e) =>
              onChange({ ...value, closeMinute: fromTimeString(e.target.value) })
            }
            className="h-9 rounded-md border border-input bg-background px-2 text-sm"
          />
        </div>
      ) : (
        <span className="text-sm text-muted-foreground">Closed</span>
      )}
      <span className="justify-self-end text-xs text-muted-foreground">
        {value.open && value.closeMinute <= value.openMinute
          ? 'Finish must be after start'
          : ''}
      </span>
    </div>
  );
}

function OverrideRow({
  slug,
  override,
  canDelete,
  timezone,
}: {
  slug: string;
  override: AvailabilityOverride;
  canDelete: boolean;
  timezone: string;
}) {
  const qc = useQueryClient();
  const delMut = useMutation({
    mutationFn: () => deleteOverride(slug, override.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['availability', slug] }),
  });

  const typeLabel = OVERRIDE_TYPE_LABEL[override.type];
  const statusBadge = STATUS_BADGE[override.status];

  return (
    <li className="flex items-start justify-between gap-3 py-3 first:pt-0 last:pb-0">
      <div className="space-y-1">
        <div className="flex items-center gap-2 text-sm">
          <span className="font-medium">{typeLabel}</span>
          <Badge variant={statusBadge.variant}>{statusBadge.label}</Badge>
        </div>
        <div className="text-sm text-muted-foreground">
          {formatRange(override.rangeStart, override.rangeEnd, timezone)}
        </div>
        {override.note ? (
          <div className="text-xs text-muted-foreground italic">
            {override.note}
          </div>
        ) : null}
      </div>
      {canDelete ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          aria-label="Remove time off"
          onClick={() => delMut.mutate()}
          disabled={delMut.isPending}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      ) : null}
    </li>
  );
}

const OVERRIDE_TYPE_LABEL: Record<AvailabilityOverride['type'], string> = {
  time_off: 'Time off',
  sick: 'Sick',
  extra: 'Extra hours',
};

const STATUS_BADGE: Record<
  AvailabilityOverride['status'],
  { label: string; variant: 'success' | 'warning' | 'destructive' }
> = {
  approved: { label: 'Approved', variant: 'success' },
  pending: { label: 'Pending', variant: 'warning' },
  rejected: { label: 'Rejected', variant: 'destructive' },
};

export function AddOverrideDialog({
  slug,
  staffId,
  staffName,
  timezone,
  willPending,
  initialDate,
  onClose,
  onSaved,
}: {
  slug: string;
  staffId: string;
  staffName: string;
  timezone: string;
  willPending: boolean;
  initialDate?: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const today = useMemo(() => localDateInTz(Date.now(), timezone), [timezone]);
  const [type, setType] = useState<AvailabilityOverride['type']>('time_off');
  const [from, setFrom] = useState<string>(initialDate ?? today);
  const [to, setTo] = useState<string>(initialDate ?? today);
  const [note, setNote] = useState<string>('');

  const saveMut = useMutation({
    mutationFn: () => {
      const start = midnightInTz(from, timezone);
      const end = midnightInTz(to, timezone) + 24 * 60 * 60 * 1000;
      return createOverride(slug, {
        staffId,
        rangeStart: start,
        rangeEnd: end,
        type,
        note: note.trim() || null,
      });
    },
    onSuccess: onSaved,
  });

  const err = saveMut.error as AvailabilityApiError | undefined;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add time off — {staffName}</DialogTitle>
          <DialogDescription>
            Block this person from being assigned new bookings during the
            selected dates.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <FormField label="Type">
            {({ id }) => (
              <select
                id={id}
                className="h-10 w-full rounded-md border border-input bg-background pl-3 pr-8 text-sm"
                value={type}
                onChange={(e) => setType(e.target.value as AvailabilityOverride['type'])}
              >
                <option value="time_off">Time off (planned)</option>
                <option value="sick">Sick (after the fact)</option>
                <option value="extra">Extra hours added</option>
              </select>
            )}
          </FormField>

          <div className="grid grid-cols-2 gap-3">
            <FormField label="From">
              {({ id }) => (
                <Input
                  id={id}
                  type="date"
                  value={from}
                  onChange={(e) => {
                    const v = e.target.value;
                    setFrom(v);
                    if (v > to) setTo(v);
                  }}
                />
              )}
            </FormField>
            <FormField label="To">
              {({ id }) => (
                <Input id={id} type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} />
              )}
            </FormField>
          </div>

          <FormField
            label="Note (optional, admin-visible only)"
            description="Doesn't show to clients. Useful context for the owner."
          >
            {({ id }) => (
              <Textarea
                id={id}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
                maxLength={300}
              />
            )}
          </FormField>

          {willPending ? (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertTitle>This will need approval</AlertTitle>
              <AlertDescription>
                Time-off approval is on for this business. Your request
                lands as Pending until an owner or admin approves it.
              </AlertDescription>
            </Alert>
          ) : null}

          {err ? (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>
                {err.code === 'has_existing_bookings'
                  ? "You've got bookings on these days"
                  : err.code === 'overlapping_override'
                    ? 'This overlaps another entry'
                    : "Couldn't save"}
              </AlertTitle>
              <AlertDescription>
                {err.code === 'has_existing_bookings'
                  ? `There ${err.detail?.count === 1 ? 'is' : 'are'} ${err.detail?.count ?? ''} confirmed booking${
                      (err.detail?.count ?? 0) === 1 ? '' : 's'
                    } on this person's calendar in the selected range. Ask an owner to reshuffle first.`
                  : err.code === 'overlapping_override'
                    ? 'You already have a time-off entry covering some of these dates. Edit or delete it first.'
                    : err.message}
              </AlertDescription>
            </Alert>
          ) : null}
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => saveMut.mutate()}
            disabled={saveMut.isPending || from > to}
          >
            {saveMut.isPending ? 'Saving…' : 'Add time off'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------ helpers --------------------------------- */

function patternIsValid(p: WeeklyPattern): boolean {
  for (const d of DAYS) {
    const row = p[d.key];
    if (!row.open) continue;
    if (row.closeMinute <= row.openMinute) return false;
  }
  return true;
}

function rulesToPattern(rules: AvailabilityRule[]): WeeklyPattern {
  const out: WeeklyPattern = {
    mon: { ...CLOSED_DAY_ROW },
    tue: { ...CLOSED_DAY_ROW },
    wed: { ...CLOSED_DAY_ROW },
    thu: { ...CLOSED_DAY_ROW },
    fri: { ...CLOSED_DAY_ROW },
    sat: { ...CLOSED_DAY_ROW },
    sun: { ...CLOSED_DAY_ROW },
  };
  for (const rule of rules) {
    for (const d of DAYS) {
      if (rule.daysOfWeekMask & d.mask) {
        out[d.key] = {
          open: true,
          openMinute: rule.startMinuteOfDay,
          closeMinute: rule.endMinuteOfDay,
        };
      }
    }
  }
  return out;
}

/**
 * Convert the per-day pattern back into a set of availability_rules
 * rows. Days with identical open/close minutes share one row (a mask
 * bitwise-ORs them); days with different times each become their own
 * row. Keeps the row count low while still letting staff pick "Tue 8-4
 * but Thu 10-6" without forcing a uniform week.
 */
function patternToRules(
  p: WeeklyPattern,
  timezone: string,
): Array<{
  daysOfWeekMask: number;
  startMinuteOfDay: number;
  endMinuteOfDay: number;
  timezone: string;
}> {
  const groups = new Map<string, { mask: number; startMinuteOfDay: number; endMinuteOfDay: number }>();
  for (const d of DAYS) {
    const row = p[d.key];
    if (!row.open) continue;
    const key = `${row.openMinute}-${row.closeMinute}`;
    const existing = groups.get(key);
    if (existing) {
      existing.mask |= d.mask;
    } else {
      groups.set(key, {
        mask: d.mask,
        startMinuteOfDay: row.openMinute,
        endMinuteOfDay: row.closeMinute,
      });
    }
  }
  return [...groups.values()].map((g) => ({
    daysOfWeekMask: g.mask,
    startMinuteOfDay: g.startMinuteOfDay,
    endMinuteOfDay: g.endMinuteOfDay,
    timezone,
  }));
}

/**
 * Build a fallback weekly pattern from a tenant's serialised
 * business_hours. Falls back to Mon-Fri 9-5 if business_hours is null
 * or fails to parse — gives a sensible "default for a new staffer"
 * starting point.
 */
function businessHoursFallback(businessHours: string | null): WeeklyPattern {
  const out: WeeklyPattern = {
    mon: { ...DEFAULT_DAY_ROW },
    tue: { ...DEFAULT_DAY_ROW },
    wed: { ...DEFAULT_DAY_ROW },
    thu: { ...DEFAULT_DAY_ROW },
    fri: { ...DEFAULT_DAY_ROW },
    sat: { ...CLOSED_DAY_ROW },
    sun: { ...CLOSED_DAY_ROW },
  };
  if (!businessHours) return out;
  try {
    const parsed = JSON.parse(businessHours) as Record<
      DayKey,
      { open: boolean; openMinute: number; closeMinute: number }
    >;
    for (const d of DAYS) {
      const v = parsed[d.key];
      if (!v) continue;
      out[d.key] = v.open
        ? { open: true, openMinute: v.openMinute, closeMinute: v.closeMinute }
        : { ...CLOSED_DAY_ROW };
    }
  } catch {
    /* keep defaults */
  }
  return out;
}

function toTimeString(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function fromTimeString(s: string): number {
  const parts = s.split(':').map(Number);
  const h = parts[0];
  const m = parts[1];
  if (!Number.isFinite(h) || !Number.isFinite(m)) return 0;
  return Math.max(0, Math.min(24 * 60, (h ?? 0) * 60 + (m ?? 0)));
}

/** YYYY-MM-DD wall-clock date for an instant in the supplied IANA zone. */
export function localDateInTz(epochMs: number, timezone: string): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(epochMs));
}

/**
 * Resolve a 'YYYY-MM-DD' to epoch ms at local midnight in the supplied
 * zone. Mirrors apps/web/src/api/admin-memberships.ts:midnightInTz —
 * we keep two copies (server-side + client-side) so the dialog can
 * preview the boundary the server will land on.
 */
function midnightInTz(date: string, timezone: string): number {
  const [y, m, d] = date.split('-').map(Number);
  if (!y || !m || !d) return Date.now();
  let candidate = Date.UTC(y, m - 1, d, 0, 0, 0, 0);
  for (let i = 0; i < 2; i++) {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).formatToParts(new Date(candidate));
    const yy = Number(parts.find((p) => p.type === 'year')?.value ?? 0);
    const mm = Number(parts.find((p) => p.type === 'month')?.value ?? 0);
    const dd = Number(parts.find((p) => p.type === 'day')?.value ?? 0);
    const hh = Number(parts.find((p) => p.type === 'hour')?.value ?? 0);
    const mi = Number(parts.find((p) => p.type === 'minute')?.value ?? 0);
    const wantMs = Date.UTC(y, m - 1, d, 0, 0, 0, 0);
    const haveMs = Date.UTC(yy, mm - 1, dd, hh % 24, mi, 0, 0);
    const delta = wantMs - haveMs;
    if (delta === 0) return candidate;
    candidate += delta;
  }
  return candidate;
}

function formatRange(start: number, end: number, timezone: string): string {
  const startDate = localDateInTz(start, timezone);
  // Subtract 1ms so a [Mon 00:00, Tue 00:00) range reads as "Monday"
  // not "Mon-Tue". The exclusive end is server-side; show inclusive.
  const endDate = localDateInTz(end - 1, timezone);
  if (startDate === endDate) {
    return new Intl.DateTimeFormat('en-AU', {
      timeZone: timezone,
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(new Date(start));
  }
  const f = new Intl.DateTimeFormat('en-AU', {
    timeZone: timezone,
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  return `${f.format(new Date(start))} – ${f.format(new Date(end - 1))}`;
}

